import { useQuery, useMutation, useQueryClient } from 'react-query'
import { get, post, Params } from 'api/fetch'
import { Draft, PaginatedResponse, DraftRequestReviewAction } from './types/types'
import { useHistory, useLocation } from 'react-router-dom'
import { useMemo } from 'react'

const apiUrl = process.env.REACT_APP_API_V2 ?? ''

const DEFAULT_PAGE = 1
const DEFAULT_LIMIT = 10

const getDrafts = async (params: Params): Promise<PaginatedResponse<Draft>> => {
  const page = parseInt(params.page || DEFAULT_PAGE.toString(), 10)
  const limit = parseInt(params.limit || DEFAULT_LIMIT.toString(), 10)
  const offset = (page - 1) * limit

  const apiParams: Params = {
    limit: limit.toString(),
    offset: offset.toString(),
  }

  if (params.status) {
    apiParams.status = params.status
  }

  const res = await get(`${apiUrl}/drop-requests/admin/drafts`, apiParams)
  if (res.status === 200 && res.parsedBody) {
    return res.parsedBody as PaginatedResponse<Draft>
  }
  throw new Error('Failed to fetch drafts')
}

const reviewDraft = async ({
  draftId,
  action,
  comment,
}: {
  draftId: number
  action: DraftRequestReviewAction
  comment?: string
}) => {
  const payload: { action: DraftRequestReviewAction; comment?: string } = { action }

  // Add comment if provided
  if (comment && comment.trim()) {
    payload.comment = comment
  }

  const res = await post(`${apiUrl}/drop-requests/drafts/${draftId}/review`, payload)

  if (res.status !== 200) {
    throw new Error('There was an error reviewing the draft')
  }

  return res.parsedBody
}

const addDraftComment = async ({ draftId, comment }: { draftId: number; comment: string }) => {
  const payload = { comment }

  const res = await post(`${apiUrl}/drop-requests/drafts/${draftId}/comments`, payload)

  if (res.status !== 204) {
    throw new Error('There was an error adding the comment')
  }

  return res.parsedBody
}

const getDraftWithAllReviews = async (draftId: number): Promise<Draft> => {
  const res = await get(`${apiUrl}/drop-requests/drafts/${draftId}/reviews`)
  if (res.status === 200 && res.parsedBody) {
    return res.parsedBody as Draft
  }
  throw new Error('Failed to fetch draft reviews')
}

export const useReviewDraftMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(reviewDraft, {
    onSuccess: () => {
      queryClient.invalidateQueries(['drafts'])
      queryClient.invalidateQueries(['recentDrafts'])
      queryClient.invalidateQueries(['batchDraftsForPetitions'])
    },
  })
}

export const useAddCommentMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(addDraftComment, {
    onSuccess: (_, { draftId }) => {
      // Only refresh the specific draft reviews, not the entire drafts list
      queryClient.invalidateQueries(['draftReviews', draftId])
      queryClient.invalidateQueries(['drafts'])
      queryClient.invalidateQueries(['batchDraftsForPetitions'])
    },
  })
}

export const useGetDraftsQuery = (initialParams: Params = {}) => {
  const history = useHistory()
  const location = useLocation()

  const currentParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    const params: Params = { ...initialParams }
    for (const [key, value] of searchParams.entries()) {
      params[key] = value
    }
    return params
  }, [location.search, initialParams])

  const setQueryParams = (newParams: Params | ((prevParams: Params) => Params)) => {
    const params = typeof newParams === 'function' ? newParams(currentParams) : newParams
    const search = new URLSearchParams(params).toString()
    history.push({ search: `?${search}` })
  }

  const draftsQuery = useQuery(['drafts', currentParams], () => getDrafts(currentParams), {
    keepPreviousData: true,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  })

  return {
    draftsQuery,
    currentParams,
    setQueryParams,
  }
}

export const useGetDraftReviewsQuery = (draftId: number) => {
  return useQuery(['draftReviews', draftId], () => getDraftWithAllReviews(draftId), {
    staleTime: 30000,
    enabled: !!draftId,
  })
}
